import React, { useState, useRef, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FolderOpen, Calendar, ChevronLeft, ChevronRight, FileText, User, ZoomIn, ZoomOut, Edit2, AlignLeft, Menu, X, Upload, Search, Filter, Trash2 } from 'lucide-react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { useProjects } from '../contexts/ProjectsContext';
import { useClients } from '../contexts/ClientsContext';
import { useFolders } from '../contexts/FoldersContext';
import { useBilling } from '../contexts/BillingContext';
import { supabase } from '../lib/supabase';

// Set up the PDF.js worker - use CDN
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface StatusOption {
  value: string;
  label: string;
  color: string;
  bgColor: string;
}

interface FolderLayoutProps {
  folderId?: string;
  folderName?: string;
}

function FolderLayout({ folderId, folderName = 'All Projects' }: FolderLayoutProps) {
  const navigate = useNavigate();
  const { projects: allProjects, updateProject, deleteProject, addFileActivity, getAllProjectsInFolderTree } = useProjects();
  const { clients } = useClients();
  const { folders } = useFolders();
  const { billingGateLoading, hasActiveBilling } = useBilling();
  
  // Get projects for this folder including all subfolders
  const baseProjects = folderId ? getAllProjectsInFolderTree(folderId, folders) : allProjects;
  
  // Alias for compatibility
  const projects = allProjects;
  
  // Get folder path for display
  const getFolderPath = (fId: string): string => {
    const folder = folders.find(f => f.id === fId);
    if (!folder || folder.parentId === null || folder.id === '00000000-0000-0000-0000-000000000001') {
      return folder?.name || '';
    }
    const parentPath = getFolderPath(folder.parentId);
    return parentPath ? `${parentPath}/${folder.name}` : folder.name;
  };
  const [pdfPages, setPdfPages] = useState<{ [key: string]: number }>({});
  const [numPages, setNumPages] = useState<{ [key: string]: number }>({});
  const [currentFileIndex, setCurrentFileIndex] = useState<{ [key: string]: number }>({});
  const [zoomLevels, setZoomLevels] = useState<{ [key: string]: number }>({});
  const [editingField, setEditingField] = useState<{ projectId: string; field: string } | null>(null);
  const [editValue, setEditValue] = useState<string>('');
  const [showStatusPicker, setShowStatusPicker] = useState<string | null>(null);
  const [showPdfSidebar, setShowPdfSidebar] = useState<string | null>(null);
  const fileInputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});
  const [isDragging, setIsDragging] = useState<string | null>(null);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [scrollStart, setScrollStart] = useState({ x: 0, y: 0 });
  const pdfWrapperRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const [showContractModal, setShowContractModal] = useState<string | null>(null);
  const [contractSizeInput, setContractSizeInput] = useState<string>('');
  const [showEstimateModal, setShowEstimateModal] = useState<string | null>(null);
  const [estimateInput, setEstimateInput] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [filterClient, setFilterClient] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filterDeadline, setFilterDeadline] = useState<string>('');
  const [filterFolder, setFilterFolder] = useState<string>('');
  const [dateRangeStart, setDateRangeStart] = useState<string>('');
  const [dateRangeEnd, setDateRangeEnd] = useState<string>('');
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [folderSearch, setFolderSearch] = useState<string>('');
  const [expandedDropdownFolders, setExpandedDropdownFolders] = useState<Set<string>>(new Set());
  const [paginationPage, setPaginationPage] = useState<number>(1);
  const projectsPerPage = 10;
  const [uploadingFiles, setUploadingFiles] = useState<string | null>(null);
  const [showSuccessModal, setShowSuccessModal] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [showErrorModal, setShowErrorModal] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<{ projectId: string; fileId: string; fileName: string; storagePath: string } | null>(null);

  const handleBillingLockedAction = () => {
    alert('You need to connect a card first before opening projects.');
    navigate('/settings/billing');
  };

  const statusOptions: StatusOption[] = [
    { value: 'estimating', label: 'Estimating', color: 'var(--yellow-text-dark)', bgColor: '#fef3c7' },
    { value: 'proposal', label: 'Proposal', color: 'var(--blue-text-dark)', bgColor: 'var(--blue-tint-medium)' },
    { value: 'accepted', label: 'Accepted', color: 'var(--green-text-dark)', bgColor: 'var(--green-tint-strong)' },
    { value: 'rejected', label: 'Rejected', color: 'var(--red-text-dark)', bgColor: 'var(--red-tint-medium)' },
  ];

  const legacyInReviewStatusOption: StatusOption = {
    value: 'in-review',
    label: 'In Review (Legacy)',
    color: 'var(--purple-text-dark)',
    bgColor: '#e9d5ff',
  };

  const getStatusOption = (status: string): StatusOption => {
    if (status === 'in-review') {
      return legacyInReviewStatusOption;
    }
    return statusOptions.find(opt => opt.value === status) || statusOptions[0];
  };

  const handleStatusChange = (projectId: string, newStatus: string, oldStatus: string) => {
    const project = allProjects.find(p => p.id === projectId);
    
    if (newStatus === 'accepted') {
      // Show modal to ask for contract size
      setContractSizeInput('');
      setShowContractModal(projectId);
      setShowStatusPicker(null);
    } else if (newStatus === 'proposal') {
      // Show modal to verify/edit estimate amount (default to 0)
      setEstimateInput('0');
      setShowEstimateModal(projectId);
      setShowStatusPicker(null);
    } else {
      // Handle transitions from accepted/rejected
      const updates: any = { status: newStatus };
      
      if (oldStatus === 'accepted') {
        if (newStatus === 'rejected') {
          updates.contractSize = 0;
        } else {
          updates.contractSize = null;
        }
      }
      
      if (newStatus === 'estimating') {
        updates.estimatedBudget = null;
      }
      
      updateProject(projectId, updates);
      setShowStatusPicker(null);
    }
  };

  const handleContractSizeSubmit = (projectId: string) => {
    const project = allProjects.find(p => p.id === projectId);
    const contractValue = parseFloat(contractSizeInput);
    
    const updates: any = { status: 'accepted' };
    
    if (contractSizeInput && !isNaN(contractValue)) {
      updates.contractSize = contractValue;
    }
    
    updateProject(projectId, updates);
    setShowContractModal(null);
    setContractSizeInput('');
  };

  const handleEstimateSubmit = (projectId: string) => {
    const estimateValue = parseFloat(estimateInput);
    
    const updates: any = { status: 'proposal' };
    
    if (estimateInput && !isNaN(estimateValue)) {
      updates.estimatedBudget = estimateValue;
    } else {
      updates.estimatedBudget = 0;
    }
    
    updateProject(projectId, updates);
    setShowEstimateModal(null);
    setEstimateInput('');
  };

  const handleSelectFile = (projectId: string, fileIndex: number) => {
    updateCurrentFileIdx(projectId, fileIndex);
    setShowPdfSidebar(null);
  };

  const handleAddFiles = (projectId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    fileInputRefs.current[projectId]?.click();
  };

  const handleFileUpload = async (projectId: string, files: FileList | null) => {
    if (!files || files.length === 0) return;

    const project = allProjects.find(p => p.id === projectId);
    if (!project) return;

    if (!supabase) {
      setErrorMessage('Supabase not configured');
      setShowErrorModal(true);
      return;
    }

    setUploadingFiles(projectId);

    try {
      // Get the current max file order
      const { data: existingFiles } = await supabase
        .from('estimation_files')
        .select('file_order')
        .eq('estimation_id', projectId)
        .order('file_order', { ascending: false })
        .limit(1);

      let nextOrder = existingFiles && existingFiles.length > 0 ? (existingFiles[0].file_order || 0) + 1 : 0;
      
      const uploadPromises = [];
      
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const timestamp = Date.now();
        const randomStr = Math.random().toString(36).substring(7);
        const filePath = `blueprints/${timestamp}_${randomStr}.pdf`;
        
        // Upload to Supabase storage and add to table in parallel
        uploadPromises.push(
          (async () => {
            const { error: uploadError } = await supabase.storage
              .from('bucket')
              .upload(filePath, file);

            if (uploadError) {
              console.error('Error uploading file:', uploadError);
              return { success: false, error: uploadError.message, fileName: file.name };
            }

            // Add to estimation_files table
            const { error: insertError } = await supabase
              .from('estimation_files')
              .insert({
                estimation_id: projectId,
                file_name: file.name,
                file_size: file.size,
                file_type: 'application/pdf',
                storage_path: filePath,
                file_order: nextOrder + i,
              });

            if (insertError) {
              console.error('Error inserting file record:', insertError);
              return { success: false, error: insertError.message, fileName: file.name };
            }

            return { success: true, fileName: file.name };
          })()
        );
      }

      const results = await Promise.all(uploadPromises);
      const successCount = results.filter(r => r.success).length;
      const failures = results.filter(r => !r.success);

      // Log activities for successful uploads
      results.forEach(result => {
        if (result.success) {
          addFileActivity(projectId, result.fileName, 'added');
        }
      });

      setUploadingFiles(null);

      if (failures.length > 0) {
        setSuccessMessage(`Uploaded ${successCount} file(s). Failed: ${failures.map(f => f.fileName).join(', ')}`);
        setShowSuccessModal(true);
      } else {
        setSuccessMessage(`Successfully uploaded ${successCount} file(s)!`);
        setShowSuccessModal(true);
      }

      // Close sidebar
      setShowPdfSidebar(null);

      // Trigger manual refetch to immediately update the UI
      setTimeout(() => {
        window.dispatchEvent(new Event('refetch-projects'));
      }, 1000);

    } catch (error) {
      console.error('Error in file upload:', error);
      setUploadingFiles(null);
      setErrorMessage('Error uploading files: ' + (error as Error).message);
      setShowErrorModal(true);
    }
  };

  const confirmDeleteFile = (projectId: string, fileId: string, fileName: string, storagePath: string) => {
    const project = allProjects.find(p => p.id === projectId);
    if (!project || !project.files) return;

    // Check if this is the last file
    if (project.files.length <= 1) {
      setErrorMessage('Cannot delete the last file. Projects must have at least one PDF.');
      setShowErrorModal(true);
      return;
    }

    setShowDeleteConfirm({ projectId, fileId, fileName, storagePath });
  };

  const handleDeleteFile = async () => {
    if (!showDeleteConfirm) return;

    const { projectId, fileId, fileName, storagePath } = showDeleteConfirm;
    setShowDeleteConfirm(null);

    const project = allProjects.find(p => p.id === projectId);
    if (!project || !project.files) return;

    if (!supabase) {
      setErrorMessage('Supabase not configured');
      setShowErrorModal(true);
      return;
    }

    setUploadingFiles(projectId);

    try {
      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from('bucket')
        .remove([storagePath]);

      if (storageError) {
        console.error('Error deleting file from storage:', storageError);
        setUploadingFiles(null);
        setErrorMessage('Error deleting file: ' + storageError.message);
        setShowErrorModal(true);
        return;
      }

      // Delete from database
      const { error: dbError } = await supabase
        .from('estimation_files')
        .delete()
        .eq('id', fileId);

      if (dbError) {
        console.error('Error deleting file record:', dbError);
        setUploadingFiles(null);
        setErrorMessage('Error deleting file: ' + dbError.message);
        setShowErrorModal(true);
        return;
      }

      // Log activity
      addFileActivity(projectId, fileName, 'removed');

      // If we deleted the currently viewed file, switch to the first file
      const currentIdx = getCurrentFileIdx(projectId);
      const deletedIdx = project.files.findIndex(f => f.id === fileId);
      if (currentIdx === deletedIdx) {
        updateCurrentFileIdx(projectId, 0);
      } else if (currentIdx > deletedIdx) {
        // Adjust the index if we deleted a file before the current one
        updateCurrentFileIdx(projectId, currentIdx - 1);
      }

      setUploadingFiles(null);
      setSuccessMessage(`Successfully deleted "${fileName}"`);
      setShowSuccessModal(true);

      // Trigger manual refetch to immediately update the UI
      setTimeout(() => {
        window.dispatchEvent(new Event('refetch-projects'));
      }, 1000);

    } catch (error) {
      console.error('Error deleting file:', error);
      setUploadingFiles(null);
      setErrorMessage('Error deleting file: ' + (error as Error).message);
      setShowErrorModal(true);
    }
  };

  const getCurrentPage = (projectId: string) => pdfPages[projectId] || 1;
  
  const updateCurrentPage = (projectId: string, page: number) => {
    setPdfPages(prev => ({ ...prev, [projectId]: page }));
  };

  const onDocumentLoadSuccess = (projectId: string, pdf: any) => {
    setNumPages(prev => ({ ...prev, [projectId]: pdf.numPages }));
  };

  const getCurrentFileIdx = (projectId: string) => currentFileIndex[projectId] || 0;
  
  const updateCurrentFileIdx = (projectId: string, index: number) => {
    setCurrentFileIndex(prev => ({ ...prev, [projectId]: index }));
    // Reset page to 1 when changing files
    setPdfPages(prev => ({ ...prev, [projectId]: 1 }));
  };

  const getZoomLevel = (projectId: string) => zoomLevels[projectId] || 1.0;

  const handleZoomIn = (projectId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setZoomLevels(prev => ({
      ...prev,
      [projectId]: Math.min((prev[projectId] || 1.0) + 0.25, 4.0)
    }));
  };

  const handleZoomOut = (projectId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setZoomLevels(prev => ({
      ...prev,
      [projectId]: Math.max((prev[projectId] || 1.0) - 0.25, 0.25)
    }));
  };

  const handleMouseDown = (projectId: string, e: React.MouseEvent<HTMLDivElement>) => {
    const wrapper = pdfWrapperRefs.current[projectId];
    if (!wrapper) return;
    
    setIsDragging(projectId);
    setDragStart({ x: e.clientX, y: e.clientY });
    setScrollStart({ x: wrapper.scrollLeft, y: wrapper.scrollTop });
    e.preventDefault();
  };

  const handleMouseMove = (projectId: string, e: React.MouseEvent<HTMLDivElement>) => {
    if (isDragging !== projectId) return;
    
    const wrapper = pdfWrapperRefs.current[projectId];
    if (!wrapper) return;

    const dx = e.clientX - dragStart.x;
    const dy = e.clientY - dragStart.y;
    
    wrapper.scrollLeft = scrollStart.x - dx;
    wrapper.scrollTop = scrollStart.y - dy;
  };

  const handleMouseUp = () => {
    setIsDragging(null);
  };

  const handleEdit = (projectId: string, field: string, currentValue: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingField({ projectId, field });
    setEditValue(currentValue);
  };

  const handleSaveEdit = (projectId: string, field: string) => {
    if (field !== 'description' && editValue.trim() === '') {
      setEditingField(null);
      return;
    }

    const updates: any = {};
    if (field === 'name') {
      updates.name = editValue.trim();
    } else if (field === 'clientName') {
      updates.clientName = editValue.trim();
    } else if (field === 'bidDeadline') {
      updates.bidDeadline = editValue;
    } else if (field === 'description') {
      updates.description = editValue.trim();
    } else if (field === 'estimatedBudget') {
      const value = parseFloat(editValue);
      updates.estimatedBudget = !isNaN(value) ? value : null;
    } else if (field === 'contractSize') {
      const value = parseFloat(editValue);
      updates.contractSize = !isNaN(value) ? value : null;
    }

    updateProject(projectId, updates);
    setEditingField(null);
    setEditValue('');
  };

  const handleCancelEdit = () => {
    setEditingField(null);
    setEditValue('');
  };

  // Reset to page 1 when filters change
  React.useEffect(() => {
    setPaginationPage(1);
  }, [searchQuery, filterClient, filterStatus, filterDeadline, filterFolder]);

  // Filter and search projects
  const filteredProjects = useMemo(() => {
    return baseProjects.filter(project => {
      // Keyword search (name and description)
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesName = project.name.toLowerCase().includes(query);
        const matchesDescription = project.description?.toLowerCase().includes(query);
        if (!matchesName && !matchesDescription) return false;
      }

      // Filter by client
      if (filterClient && project.clientName !== filterClient) {
        return false;
      }

      // Filter by status
      if (filterStatus && project.status !== filterStatus) {
        return false;
      }

      // Filter by folder (include subfolders)
      if (filterFolder) {
        const isInFolderTree = (projectFolderId: string, targetFolderId: string): boolean => {
          if (projectFolderId === targetFolderId) return true;

          // Check if project is in any subfolder
          const projectFolder = folders.find(f => f.id === projectFolderId);
          if (!projectFolder || !projectFolder.parentId) return false;

          return isInFolderTree(projectFolder.parentId, targetFolderId);
        };

        if (!isInFolderTree(project.folderId, filterFolder)) {
          return false;
        }
      }

      // Filter by deadline
      if (filterDeadline) {
        if (filterDeadline === 'has-deadline' && !project.bidDeadline) return false;
        if (filterDeadline === 'no-deadline' && project.bidDeadline) return false;
        if (filterDeadline === 'overdue') {
          if (!project.bidDeadline) return false;
          const deadline = new Date(project.bidDeadline);
          if (deadline >= new Date()) return false;
        }
        if (filterDeadline === 'date-range') {
          // Show no projects until at least one date is selected
          if (!dateRangeStart && !dateRangeEnd) return false;

          if (!project.bidDeadline) return false;
          const deadline = new Date(project.bidDeadline);
          if (dateRangeStart) {
            const start = new Date(dateRangeStart);
            if (deadline < start) return false;
          }
          if (dateRangeEnd) {
            const end = new Date(dateRangeEnd);
            if (deadline > end) return false;
          }
        }
      }

      return true;
    });
  }, [baseProjects, searchQuery, filterClient, filterStatus, filterDeadline, filterFolder, folderId, dateRangeStart, dateRangeEnd, folders]);

  // Get unique client names for filter
  const uniqueClients = useMemo(() => {
    const clientNames = baseProjects.map(p => p.clientName);
    const uniqueSet = new Set<string>();
    clientNames.forEach(name => uniqueSet.add(name));
    const clients: string[] = [];
    uniqueSet.forEach(name => clients.push(name));
    return clients.sort();
  }, [baseProjects]);

  // Get subfolders for this folder (for filtering)
  // Root should be the current folder itself plus its descendants
  const subFolders = useMemo(() => {
    if (!folderId) {
      // All Projects view: show all non-root folders
      return folders.filter(f => f.id !== '00000000-0000-0000-0000-000000000001');
    } else {
      // Specific folder: include current folder and all descendants
      const getDescendantsIncludingSelf = (id: string): any[] => {
        const folder = folders.find(f => f.id === id);
        if (!folder) return [];
        
        const children = folders.filter(f => f.parentId === id);
        let descendants: any[] = [folder]; // Include self
        children.forEach(child => {
          descendants = [...descendants, ...getDescendantsIncludingSelf(child.id)];
        });
        return descendants;
      };
      return getDescendantsIncludingSelf(folderId);
    }
  }, [folders, folderId]);

  // Filter folders by search
  const filteredFolders = useMemo(() => {
    if (!folderSearch) return subFolders;
    const query = folderSearch.toLowerCase();
    return subFolders.filter(f => {
      const path = getFolderPath(f.id).toLowerCase();
      return path.includes(query);
    });
  }, [subFolders, folderSearch]);

  const toggleDropdownFolder = (folderId: string) => {
    const newExpanded = new Set(expandedDropdownFolders);
    if (newExpanded.has(folderId)) {
      newExpanded.delete(folderId);
    } else {
      newExpanded.add(folderId);
    }
    setExpandedDropdownFolders(newExpanded);
  };

  const renderFolderTreeItem = (folder: any, level: number = 0, isRoot: boolean = false): React.ReactElement | null => {
    const children = filteredFolders.filter(f => f.parentId === folder.id);
    const isExpanded = expandedDropdownFolders.has(folder.id);
    const hasChildren = children.length > 0;

    return (
      <div key={folder.id}>
        <button
          className={`filter-folder-tree-item ${filterFolder === folder.id ? 'active' : ''} ${isRoot ? 'root-folder' : ''}`}
          style={{ paddingLeft: `${0.75 + level * 1.25}rem` }}
          onClick={() => {
            setFilterFolder(folder.id);
            setOpenDropdown(null);
            setFolderSearch('');
          }}
        >
          {hasChildren && (
            <ChevronRight
              size={14}
              className={`folder-tree-chevron ${isExpanded ? 'expanded' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                toggleDropdownFolder(folder.id);
              }}
            />
          )}
          <FolderOpen size={14} style={{ marginLeft: hasChildren ? 0 : '18px' }} />
          <span>{isRoot ? `${folder.name} (Current)` : folder.name}</span>
        </button>
        {isExpanded && children.map(child => renderFolderTreeItem(child, level + 1, false))}
      </div>
    );
  };

  return (
    <div className="dashboard-container">
      {/* Loading Overlay */}
      {uploadingFiles && (
        <div className="modal-overlay" style={{ zIndex: 10001 }}>
          <div className="loading-spinner-container">
            <div className="loading-spinner"></div>
            <p className="loading-text">Processing files...</p>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="modal-overlay" style={{ zIndex: 10001 }} onClick={() => setShowSuccessModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Success</h2>
              <button className="modal-close" onClick={() => setShowSuccessModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <p>{successMessage}</p>
            </div>
            <div className="modal-footer">
              <button className="btn-primary" onClick={() => setShowSuccessModal(false)}>
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {showErrorModal && (
        <div className="modal-overlay" style={{ zIndex: 10001 }} onClick={() => setShowErrorModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title" style={{ color: '#dc2626' }}>Error</h2>
              <button className="modal-close" onClick={() => setShowErrorModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <p>{errorMessage}</p>
            </div>
            <div className="modal-footer">
              <button className="btn-primary" onClick={() => setShowErrorModal(false)}>
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="modal-overlay" style={{ zIndex: 10001 }} onClick={() => setShowDeleteConfirm(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title" style={{ color: '#dc2626' }}>Delete File</h2>
              <button className="modal-close" onClick={() => setShowDeleteConfirm(null)}>×</button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete "{showDeleteConfirm.fileName}"?</p>
              <p style={{ marginTop: '0.5rem', color: 'var(--text-muted)', fontSize: '0.875rem' }}>This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowDeleteConfirm(null)}>
                Cancel
              </button>
              <button
                className="btn-primary"
                style={{ backgroundColor: '#dc2626', borderColor: '#dc2626' }}
                onClick={handleDeleteFile}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Contract Size Modal */}
      {showContractModal && (
        <div className="modal-overlay" onClick={() => setShowContractModal(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Enter Contract Size</h2>
              <button className="modal-close" onClick={() => setShowContractModal(null)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Contract Amount ($)</label>
                <input
                  type="number"
                  className="form-input-large"
                  placeholder="Enter contract size..."
                  value={contractSizeInput}
                  onChange={(e) => setContractSizeInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleContractSizeSubmit(showContractModal);
                    }
                  }}
                  autoFocus
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowContractModal(null)}>
                Skip
              </button>
              <button className="btn-primary" onClick={() => handleContractSizeSubmit(showContractModal)}>
                Save & Accept
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Estimate Amount Modal */}
      {showEstimateModal && (
        <div className="modal-overlay" onClick={() => setShowEstimateModal(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Verify Estimate Amount</h2>
              <button className="modal-close" onClick={() => setShowEstimateModal(null)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Estimate Amount ($)</label>
                <input
                  type="number"
                  className="form-input-large"
                  placeholder="Enter estimate amount..."
                  value={estimateInput}
                  onChange={(e) => setEstimateInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleEstimateSubmit(showEstimateModal);
                    }
                  }}
                  autoFocus
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => {
                updateProject(showEstimateModal, { status: 'proposal', estimatedBudget: 0 });
                setShowEstimateModal(null);
                setEstimateInput('');
              }}>
                Set to $0
              </button>
              <button className="btn-primary" onClick={() => handleEstimateSubmit(showEstimateModal)}>
                Save & Continue
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="dashboard-main">
        <div className="page-header-with-search">
          <h1 className="dashboard-page-title" style={{ marginBottom: '0' }}>{folderName}</h1>
          
          {/* Search and Filter Bar */}
          <div className="search-filter-container">
            <div className="search-bar-wrapper">
              <Search size={20} className="search-icon" />
              <input
                type="text"
                className="search-input-main"
                placeholder="Search by name or description..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button className="clear-search-btn" onClick={() => setSearchQuery('')}>
                  <X size={16} />
                </button>
              )}
            </div>
            
            <button 
              className={`filter-toggle-btn ${showFilters ? 'active' : ''}`}
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter size={18} />
              Filters
              {(filterClient || filterStatus || filterDeadline || filterFolder) && (
                <span className="filter-active-dot" />
              )}
            </button>
          </div>

          {/* Filter Options */}
          {showFilters && (
            <div className="filters-panel">
              {/* Client Filter */}
              <div className="filter-group">
                <label className="filter-label">Client</label>
                <div className="custom-filter-dropdown">
                  <button
                    className="filter-dropdown-trigger"
                    onClick={() => setOpenDropdown(openDropdown === 'client' ? null : 'client')}
                  >
                    <span>{filterClient || 'All Clients'}</span>
                    <ChevronRight className={`dropdown-chevron ${openDropdown === 'client' ? 'open' : ''}`} size={16} />
                  </button>
                  {openDropdown === 'client' && (
                    <div className="filter-dropdown-menu">
                      <button 
                        className={`filter-dropdown-item ${!filterClient ? 'active' : ''}`}
                        onClick={() => {
                          setFilterClient('');
                          setOpenDropdown(null);
                        }}
                      >
                        All Clients
                      </button>
                      {uniqueClients.map(client => (
                        <button
                          key={client}
                          className={`filter-dropdown-item ${filterClient === client ? 'active' : ''}`}
                          onClick={() => {
                            setFilterClient(client);
                            setOpenDropdown(null);
                          }}
                        >
                          {client}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Status Filter */}
              <div className="filter-group">
                <label className="filter-label">Status</label>
                <div className="custom-filter-dropdown">
                  <button
                    className="filter-dropdown-trigger"
                    onClick={() => setOpenDropdown(openDropdown === 'status' ? null : 'status')}
                  >
                    <span>{filterStatus ? statusOptions.find(s => s.value === filterStatus)?.label : 'All Statuses'}</span>
                    <ChevronRight className={`dropdown-chevron ${openDropdown === 'status' ? 'open' : ''}`} size={16} />
                  </button>
                  {openDropdown === 'status' && (
                    <div className="filter-dropdown-menu">
                      <button 
                        className={`filter-dropdown-item ${!filterStatus ? 'active' : ''}`}
                        onClick={() => {
                          setFilterStatus('');
                          setOpenDropdown(null);
                        }}
                      >
                        All Statuses
                      </button>
                      {statusOptions.map(status => (
                        <button
                          key={status.value}
                          className={`filter-dropdown-item ${filterStatus === status.value ? 'active' : ''}`}
                          onClick={() => {
                            setFilterStatus(status.value);
                            setOpenDropdown(null);
                          }}
                        >
                          {status.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Deadline Filter */}
              <div className="filter-group">
                <label className="filter-label">Deadline</label>
                <div className="custom-filter-dropdown">
                  <button
                    className="filter-dropdown-trigger"
                    onClick={() => setOpenDropdown(openDropdown === 'deadline' ? null : 'deadline')}
                  >
                    <span>
                      {filterDeadline === 'has-deadline' ? 'Has Deadline' :
                       filterDeadline === 'no-deadline' ? 'No Deadline' :
                       filterDeadline === 'overdue' ? 'Overdue' :
                       filterDeadline === 'date-range' ? 'Date Range' : 'All Deadlines'}
                    </span>
                    <ChevronRight className={`dropdown-chevron ${openDropdown === 'deadline' ? 'open' : ''}`} size={16} />
                  </button>
                  {openDropdown === 'deadline' && (
                    <div className="filter-dropdown-menu">
                      <button 
                        className={`filter-dropdown-item ${!filterDeadline ? 'active' : ''}`}
                        onClick={() => {
                          setFilterDeadline('');
                          setOpenDropdown(null);
                        }}
                      >
                        All Deadlines
                      </button>
                      <button 
                        className={`filter-dropdown-item ${filterDeadline === 'has-deadline' ? 'active' : ''}`}
                        onClick={() => {
                          setFilterDeadline('has-deadline');
                          setOpenDropdown(null);
                        }}
                      >
                        Has Deadline
                      </button>
                      <button 
                        className={`filter-dropdown-item ${filterDeadline === 'no-deadline' ? 'active' : ''}`}
                        onClick={() => {
                          setFilterDeadline('no-deadline');
                          setOpenDropdown(null);
                        }}
                      >
                        No Deadline
                      </button>
                      <button
                        className={`filter-dropdown-item ${filterDeadline === 'overdue' ? 'active' : ''}`}
                        onClick={() => {
                          setFilterDeadline('overdue');
                          setOpenDropdown(null);
                        }}
                      >
                        Overdue
                      </button>
                      <button
                        className={`filter-dropdown-item ${filterDeadline === 'date-range' ? 'active' : ''}`}
                        onClick={() => {
                          setFilterDeadline('date-range');
                        }}
                      >
                        Date Range
                      </button>
                      {filterDeadline === 'date-range' && (
                        <div className="date-range-calendar-container" onClick={(e) => e.stopPropagation()}>
                          <div className="date-range-header">
                            <div className="date-range-info-text">
                              Select a date range to filter projects
                            </div>
                          </div>
                          <div className="date-range-selectors">
                            <div className="date-range-field">
                              <label className="date-range-field-label">Start Date</label>
                              <input
                                type="date"
                                className="date-range-calendar-input"
                                value={dateRangeStart}
                                onChange={(e) => setDateRangeStart(e.target.value)}
                                placeholder="Select start date"
                              />
                            </div>
                            <div className="date-range-divider">
                              <ChevronRight size={16} />
                            </div>
                            <div className="date-range-field">
                              <label className="date-range-field-label">End Date</label>
                              <input
                                type="date"
                                className="date-range-calendar-input"
                                value={dateRangeEnd}
                                onChange={(e) => setDateRangeEnd(e.target.value)}
                                placeholder="Select end date"
                                min={dateRangeStart || undefined}
                              />
                            </div>
                          </div>
                          {(dateRangeStart || dateRangeEnd) && (
                            <div className="date-range-actions">
                              <button
                                className="date-range-clear-btn"
                                onClick={() => {
                                  setDateRangeStart('');
                                  setDateRangeEnd('');
                                }}
                              >
                                Clear Dates
                              </button>
                              <button
                                className="date-range-apply-btn"
                                onClick={() => setOpenDropdown(null)}
                              >
                                Apply Filter
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Folder Filter with Search */}
              {subFolders.length > 0 && (
                <div className="filter-group">
                  <label className="filter-label">Folder</label>
                  <div className="custom-filter-dropdown">
                    <button
                      className="filter-dropdown-trigger"
                      onClick={() => {
                        setOpenDropdown(openDropdown === 'folder' ? null : 'folder');
                        if (openDropdown !== 'folder') setFolderSearch('');
                      }}
                    >
                      <span>
                        {filterFolder ? (folders.find(f => f.id === filterFolder)?.name || 'Unknown Folder') : 'All Folders'}
                      </span>
                      <ChevronRight className={`dropdown-chevron ${openDropdown === 'folder' ? 'open' : ''}`} size={16} />
                    </button>
                    {openDropdown === 'folder' && (
                      <div className="filter-dropdown-menu">
                        <div className="dropdown-search-wrapper">
                          <Search size={16} className="dropdown-search-icon" />
                          <input
                            type="text"
                            className="dropdown-search-input"
                            placeholder="Search folders..."
                            value={folderSearch}
                            onChange={(e) => setFolderSearch(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                        <button 
                          className={`filter-dropdown-item ${!filterFolder ? 'active' : ''}`}
                          onClick={() => {
                            setFilterFolder('');
                            setOpenDropdown(null);
                            setFolderSearch('');
                          }}
                        >
                          All Folders
                        </button>
                        {filteredFolders.length === 0 && folderSearch ? (
                          <div className="dropdown-empty-state">No folders found</div>
                        ) : (
                          <>
                            {!folderSearch && (
                              // Show tree structure when not searching
                              <>
                                {folderId ? (
                                  // In specific folder: show current folder as root
                                  (() => {
                                    const currentFolder = folders.find(f => f.id === folderId);
                                    return currentFolder ? renderFolderTreeItem(currentFolder, 0, true) : null;
                                  })()
                                ) : (
                                  // In All Projects: show all root folders
                                  filteredFolders
                                    .filter(f => f.parentId === '00000000-0000-0000-0000-000000000001')
                                    .map(folder => renderFolderTreeItem(folder, 0, false))
                                )}
                              </>
                            )}
                            {folderSearch && (
                              // Show flat list when searching
                              filteredFolders.map(folder => (
                                <button
                                  key={folder.id}
                                  className={`filter-dropdown-item ${filterFolder === folder.id ? 'active' : ''}`}
                                  onClick={() => {
                                    setFilterFolder(folder.id);
                                    setOpenDropdown(null);
                                    setFolderSearch('');
                                  }}
                                >
                                  <FolderOpen size={14} style={{ marginRight: '0.5rem', display: 'inline' }} />
                                  {getFolderPath(folder.id)}
                                </button>
                              ))
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              <button
                className="clear-filters-btn"
                onClick={() => {
                  setFilterClient('');
                  setFilterStatus('');
                  setFilterDeadline('');
                  setFilterFolder('');
                  setFolderSearch('');
                  setDateRangeStart('');
                  setDateRangeEnd('');
                }}
              >
                Clear All Filters
              </button>
            </div>
          )}

        </div>

        {/* Pagination */}
        {filteredProjects.length > projectsPerPage && (
          <div className="pagination-controls">
            <button
              className="pagination-btn"
              onClick={() => setPaginationPage(p => Math.max(1, p - 1))}
              disabled={paginationPage === 1}
            >
              <ChevronLeft size={18} />
              Previous
            </button>
            <span className="pagination-info">
              Page {paginationPage} of {Math.ceil(filteredProjects.length / projectsPerPage)}
            </span>
            <button
              className="pagination-btn"
              onClick={() => setPaginationPage(p => Math.min(Math.ceil(filteredProjects.length / projectsPerPage), p + 1))}
              disabled={paginationPage >= Math.ceil(filteredProjects.length / projectsPerPage)}
            >
              Next
              <ChevronRight size={18} />
            </button>
          </div>
        )}

        <div className="all-projects-grid">
          {filteredProjects.length === 0 ? (
            <div className="empty-state-large">
              {baseProjects.length === 0 ? (
                <>
              <FolderOpen size={64} style={{ color: 'var(--text-faint)', marginBottom: '1rem' }} />
              <p className="empty-text">No projects yet</p>
              <p className="empty-subtext">Click the + button to create your first project</p>
                </>
              ) : (
                <>
                  <Search size={64} style={{ color: 'var(--text-faint)', marginBottom: '1rem' }} />
                  <p className="empty-text">No projects found</p>
                  <p className="empty-subtext">Try adjusting your search or filters</p>
                </>
              )}
            </div>
          ) : (
            filteredProjects
              .slice((paginationPage - 1) * projectsPerPage, paginationPage * projectsPerPage)
              .map((project) => {
              const currentPage = getCurrentPage(project.id);
              const fileIdx = getCurrentFileIdx(project.id);
              const currentFile = project.files && project.files.length > 0 ? project.files[fileIdx] : null;
              const totalPages = numPages[project.id] || 1;
              const totalFiles = project.files?.length || 0;
              const zoomLevel = getZoomLevel(project.id);
              const client = clients.find(c => c.id === project.clientId);
              
              return (
                <div key={project.id} className="project-card-wide-large">
                  {/* PDF Preview Area */}
                  <div className="pdf-preview-area-large">
                    {currentFile ? (
                      <>
                        {/* PDF Header with File Name and Navigation */}
                        <div className="pdf-header">
                          <div className="pdf-file-name-display">
                            <FileText size={18} />
                            <span>{currentFile.name}</span>
                            {totalFiles > 1 && (
                              <span className="pdf-file-badge">{fileIdx + 1} of {totalFiles}</span>
                            )}
                            </div>
                          <div className="pdf-header-nav-arrows">
                          {totalFiles > 1 && (
                            <>
                              <button
                                  className="pdf-arrow-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  updateCurrentFileIdx(project.id, Math.max(0, fileIdx - 1));
                                }}
                                disabled={fileIdx === 0}
                                title="Previous file"
                              >
                                  <ChevronLeft size={20} />
                              </button>
                              <button
                                  className="pdf-arrow-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  updateCurrentFileIdx(project.id, Math.min(totalFiles - 1, fileIdx + 1));
                                }}
                                disabled={fileIdx === totalFiles - 1}
                                title="Next file"
                              >
                                  <ChevronRight size={20} />
                                </button>
                              </>
                            )}
                            <button
                              className="pdf-hamburger-btn-header"
                              onClick={(e) => {
                                e.stopPropagation();
                                setShowPdfSidebar(showPdfSidebar === project.id ? null : project.id);
                              }}
                              title="View all files"
                            >
                              <Menu size={20} />
                            </button>
                          </div>
                        </div>

                        {/* PDF Sidebar */}
                        {showPdfSidebar === project.id && (
                          <>
                            <div 
                              className="pdf-sidebar-overlay"
                              onClick={(e) => {
                                e.stopPropagation();
                                setShowPdfSidebar(null);
                              }}
                            />
                            <div className="pdf-sidebar">
                              <div className="pdf-sidebar-header">
                                <h3>Project Files ({totalFiles})</h3>
                                <button
                                  className="pdf-sidebar-close"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setShowPdfSidebar(null);
                                  }}
                                >
                                  <X size={20} />
                                </button>
                              </div>
                              <div className="pdf-sidebar-content">
                                {project.files?.map((file, idx) => (
                                  <div key={file.id} className="pdf-sidebar-item-wrapper">
                                    <button
                                      className={`pdf-sidebar-item ${idx === fileIdx ? 'active' : ''}`}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleSelectFile(project.id, idx);
                                      }}
                                    >
                                      <FileText size={18} />
                                      <div className="pdf-sidebar-item-info">
                                        <span className="pdf-sidebar-item-name">{file.name}</span>
                                        <span className="pdf-sidebar-item-size">
                                          {(file.size / 1024 / 1024).toFixed(2)} MB
                                        </span>
                                      </div>
                                      {idx === fileIdx && (
                                        <div className="pdf-sidebar-item-indicator">
                                          <div className="active-dot" />
                                        </div>
                                      )}
                                    </button>
                                    {totalFiles > 1 && (
                                      <button
                                        className="pdf-sidebar-delete-btn"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          confirmDeleteFile(project.id, file.id, file.name, file.storagePath);
                                        }}
                                        title="Delete file"
                                      >
                                        <Trash2 size={16} />
                                      </button>
                                    )}
                                  </div>
                                ))}
                                
                                {/* Add Files Button */}
                                <button
                                  className="pdf-sidebar-add-btn"
                                  onClick={(e) => handleAddFiles(project.id, e)}
                                >
                                  <Upload size={18} />
                                  <span>Add More Files</span>
                              </button>
                                
                                {/* Hidden File Input */}
                                <input
                                  type="file"
                                  ref={(el) => { 
                                    fileInputRefs.current[project.id] = el;
                                  }}
                                  style={{ display: 'none' }}
                                  accept=".pdf"
                                  multiple
                                  onChange={(e) => handleFileUpload(project.id, e.target.files)}
                                />
                              </div>
                            </div>
                            </>
                          )}

                        {/* PDF Content */}
                        <div 
                          className="pdf-content-wrapper"
                          ref={(el) => { pdfWrapperRefs.current[project.id] = el; }}
                          onMouseDown={(e) => handleMouseDown(project.id, e)}
                          onMouseMove={(e) => handleMouseMove(project.id, e)}
                          onMouseUp={handleMouseUp}
                          onMouseLeave={handleMouseUp}
                          style={{ cursor: isDragging === project.id ? 'grabbing' : (zoomLevel > 1 ? 'grab' : 'default') }}
                        >
                          <div className="pdf-container">
                            {(() => {
                              const urlCandidate = (currentFile as any)?.url || '';
                              const mime = (currentFile as any)?.file_type || (currentFile as any)?.type || '';
                              const isPdf = !!urlCandidate && mime === 'application/pdf';

                              if (!isPdf) {
                                return (
                                  <div className="pdf-placeholder">
                                    <FileText size={64} style={{ color: 'var(--text-faint)' }} />
                                    <span>Preview not available</span>
                                  </div>
                                );
                              }

                              return (
                                <Document
                                  key={urlCandidate}
                                  file={urlCandidate}
                                  onLoadSuccess={(pdf) => onDocumentLoadSuccess(project.id, pdf)}
                                  onLoadError={(err) => {
                                    console.error('Failed to load PDF:', err);
                                  }}
                                  error={<div className="pdf-error">Failed to load PDF</div>}
                                  className="pdf-document"
                                  loading={<div style={{ display: 'none' }} />}
                                >
                                  {(() => {
                                    const total = (numPages as any)?.[project.id] || 0;
                                    if (!urlCandidate || total <= 0) return null;
                                    return (
                                      <Page
                                        pageNumber={currentPage}
                                        width={900 * zoomLevel}
                                        renderTextLayer={false}
                                        renderAnnotationLayer={false}
                                        className="pdf-page-render"
                                        loading=""
                                        error={<div className="pdf-error">Failed to render page</div>}
                                      />
                                    );
                                  })()}
                                </Document>
                              );
                            })()}
                          </div>
                        </div>
                        
                        {/* Enhanced PDF Controls - Below PDF */}
                        <div className="pdf-controls-bottom">
                          {/* Zoom Controls */}
                          <div className="pdf-control-group">
                            <button
                              className="pdf-control-btn"
                              onClick={(e) => handleZoomOut(project.id, e)}
                              disabled={zoomLevel <= 0.25}
                              title="Zoom out"
                            >
                              <ZoomOut size={18} />
                            </button>
                            <span className="pdf-zoom-indicator">{Math.round(zoomLevel * 100)}%</span>
                            <button
                              className="pdf-control-btn"
                              onClick={(e) => handleZoomIn(project.id, e)}
                              disabled={zoomLevel >= 4.0}
                              title="Zoom in"
                            >
                              <ZoomIn size={18} />
                            </button>
                          </div>
                          
                          {/* Page navigation */}
                          <div className="pdf-divider" />
                          <div className="pdf-control-group">
                              <button
                              className="pdf-control-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  updateCurrentPage(project.id, Math.max(1, currentPage - 1));
                                }}
                                disabled={currentPage === 1}
                                title="Previous page"
                              >
                                <ChevronLeft size={18} />
                              </button>
                            <div className="pdf-page-info">
                              <span className="pdf-info-label">Page</span>
                              <span className="pdf-info-value">{currentPage} / {totalPages}</span>
                            </div>
                              <button
                              className="pdf-control-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  updateCurrentPage(project.id, Math.min(totalPages, currentPage + 1));
                                }}
                                disabled={currentPage === totalPages}
                                title="Next page"
                              >
                                <ChevronRight size={18} />
                              </button>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="pdf-placeholder">
                        <FileText size={64} style={{ color: 'var(--text-faint)' }} />
                        <span>No files uploaded</span>
                      </div>
                    )}
                  </div>

                  {/* Project Info */}
                  <div className="project-info-section-enhanced">
                    {/* Editable Project Name */}
                    <div className="editable-field-group">
                      <div className="field-label-header">Project Name</div>
                      {editingField?.projectId === project.id && editingField?.field === 'name' ? (
                        <div className="inline-edit-container">
                          <input
                            type="text"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                handleSaveEdit(project.id, 'name');
                              } else if (e.key === 'Escape') {
                                handleCancelEdit();
                              }
                            }}
                            onBlur={() => handleSaveEdit(project.id, 'name')}
                            className="inline-edit-input-large"
                            autoFocus
                          />
                        </div>
                      ) : (
                        <div className="editable-field-display">
                          <h3 className="project-name-large-editable">{project.name}</h3>
                          <button
                            className="edit-field-btn"
                            onClick={(e) => handleEdit(project.id, 'name', project.name, e)}
                            title="Edit project name"
                          >
                            <Edit2 size={16} />
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Editable Client Name */}
                    <div className="editable-field-group">
                      <div className="field-label">Client</div>
                      {editingField?.projectId === project.id && editingField?.field === 'clientName' ? (
                        <div className="inline-edit-container">
                          <input
                            type="text"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                handleSaveEdit(project.id, 'clientName');
                              } else if (e.key === 'Escape') {
                                handleCancelEdit();
                              }
                            }}
                            onBlur={() => handleSaveEdit(project.id, 'clientName')}
                            className="inline-edit-input"
                            autoFocus
                          />
                        </div>
                      ) : (
                        <div className="editable-field-display">
                          <div className="project-meta-field">
                            <span className="field-value">{project.clientName}</span>
                          </div>
                          <button
                            className="edit-field-btn"
                            onClick={(e) => handleEdit(project.id, 'clientName', project.clientName, e)}
                            title="Edit client name"
                          >
                            <Edit2 size={14} />
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Estimate Amount (Read-only) */}
                    <div className="editable-field-group">
                      <div className="field-label">Estimate Amount</div>
                      <div className="editable-field-display">
                        <div className="project-meta-field">
                          <span className="field-value">
                            {project.estimatedBudget ? `$${project.estimatedBudget.toLocaleString()}` : 'Not set'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Contract Size (Read-only) */}
                    <div className="editable-field-group">
                      <div className="field-label">Contract Size</div>
                      <div className="editable-field-display">
                        <div className="project-meta-field">
                          <span className="field-value">
                            {project.contractSize ? `$${project.contractSize.toLocaleString()}` : 'Not set'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Editable Bid Deadline */}
                    <div className="editable-field-group">
                      <div className="field-label">Bid Deadline</div>
                      {editingField?.projectId === project.id && editingField?.field === 'bidDeadline' ? (
                        <div className="inline-edit-container">
                          <input
                            type="date"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                handleSaveEdit(project.id, 'bidDeadline');
                              } else if (e.key === 'Escape') {
                                handleCancelEdit();
                              }
                            }}
                            onBlur={() => handleSaveEdit(project.id, 'bidDeadline')}
                            className="inline-edit-input"
                            autoFocus
                          />
                        </div>
                      ) : (
                        <div className="editable-field-display">
                          <div className="project-meta-field">
                            <span className="field-value">
                              {project.bidDeadline ? (
                                (() => {
                                  const [year, month, day] = project.bidDeadline.split('-');
                                  const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                                  return date.toLocaleDateString('en-US', {
                          month: 'long',
                          day: 'numeric',
                          year: 'numeric'
                                  });
                                })()
                              ) : (
                                'No deadline set'
                              )}
                            </span>
                          </div>
                          <button
                            className="edit-field-btn"
                            onClick={(e) => handleEdit(project.id, 'bidDeadline', project.bidDeadline || '', e)}
                            title="Edit bid deadline"
                          >
                            <Edit2 size={14} />
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Editable Description */}
                    <div className="editable-field-group">
                      <div className="field-label">Project Description</div>
                      {editingField?.projectId === project.id && editingField?.field === 'description' ? (
                        <div className="inline-edit-container">
                          <textarea
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Escape') {
                                handleCancelEdit();
                              }
                            }}
                            onBlur={() => handleSaveEdit(project.id, 'description')}
                            className="inline-edit-textarea"
                            rows={3}
                            placeholder="Add a description..."
                            autoFocus
                          />
                        </div>
                      ) : (
                        <div className="editable-field-display">
                          <div className="project-meta-field">
                            <span className="field-value description-text">
                              {project.description || 'No description'}
                            </span>
                          </div>
                          <button
                            className="edit-field-btn"
                            onClick={(e) => handleEdit(project.id, 'description', project.description || '', e)}
                            title="Edit description"
                          >
                            <Edit2 size={14} />
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Custom Status Selector */}
                    <div className="editable-field-group">
                      <div className="field-label">Project Status</div>
                      <div className={`status-selector-wrapper ${showStatusPicker === project.id ? 'dropdown-open' : ''}`}>
                        <button
                          className="status-current-btn"
                          style={{
                            backgroundColor: getStatusOption(project.status).bgColor,
                            color: getStatusOption(project.status).color,
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowStatusPicker(showStatusPicker === project.id ? null : project.id);
                          }}
                        >
                          {getStatusOption(project.status).label}
                        </button>
                        
                        {showStatusPicker === project.id && (
                          <div className="status-picker-dropdown">
                            {statusOptions.map((option) => (
                              <button
                                key={option.value}
                                className={`status-option-btn ${project.status === option.value ? 'active' : ''}`}
                                style={{
                                  backgroundColor: option.bgColor,
                                  color: option.color,
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleStatusChange(project.id, option.value, project.status);
                                }}
                              >
                                {option.label}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="project-actions-buttons">
                      <button
                        className="btn-delete-project"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (window.confirm(`Are you sure you want to delete "${project.name}"?`)) {
                            deleteProject(project.id);
                          }
                        }}
                      >
                        Delete Project
                      </button>
                      <button
                        className="btn-open-takeoff"
                        onClick={async (e) => {
                          e.stopPropagation();
                          if (billingGateLoading || !hasActiveBilling) {
                            handleBillingLockedAction();
                            return;
                          }
                          // Update last_opened_at before navigating
                          try {
                            await supabase
                              .from('estimations')
                              .update({ last_opened_at: new Date().toISOString() })
                              .eq('id', project.id);
                          } catch (error) {
                            console.error('Error updating last_opened_at:', error);
                          }
                          navigate(`/estimation/${project.id}`);
                        }}
                        disabled={billingGateLoading || !hasActiveBilling}
                        title={
                          billingGateLoading
                            ? 'Checking billing status...'
                            : hasActiveBilling
                            ? 'Open project'
                            : 'You need to connect a card first'
                        }
                      >
                        Open Project
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

// Wrapper component for backward compatibility
function AllProjects() {
  return <FolderLayout folderId={undefined} folderName="All Projects" />;
}

export default AllProjects;
export { FolderLayout };
